import React, { useState } from 'react';
import { writeBatch, collection, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './RequestPayoutModal.css';

const UPI_REGEX = /^[a-zA-Z0-9.\-_]{2,49}@[a-zA-Z]{2,49}$/;
const PHONE_REGEX = /^[6-9]\d{9}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RequestPayoutModal = ({ currentUser, balance, onClose }) => {
  const [email, setEmail] = useState(currentUser?.email || '');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [upiId, setUpiId] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!EMAIL_REGEX.test(email.trim())) newErrors.email = 'Enter a valid email address.';
    if (!PHONE_REGEX.test(phoneNumber.trim())) newErrors.phoneNumber = 'Enter a valid 10-digit Indian mobile number.';
    if (!UPI_REGEX.test(upiId.trim())) newErrors.upiId = 'Enter a valid UPI ID (e.g. name@bank).';
    if (!accountHolderName.trim() || accountHolderName.trim().length < 2) newErrors.accountHolderName = 'Enter the account holder name.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (balance < 100) {
      setSubmitError('Your balance is below the ₹100 minimum required for payout.');
      return;
    }
    if (!validate()) return;

    setSubmitting(true);
    try {
      const batch = writeBatch(db);
      const payoutRef = doc(collection(db, 'Payouts'));
      batch.set(payoutRef, {
        userId: currentUser.uid,
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        upiId: upiId.trim(),
        accountHolderName: accountHolderName.trim(),
        amount: balance,
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      const userRef = doc(db, 'users', currentUser.uid);
      batch.update(userRef, { payoutStatus: 'pending' });

      await batch.commit();
      onClose();
    } catch (err) {
      console.error('Error submitting payout request:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="payout-modal-overlay" onClick={onClose}>
      <div className="payout-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payout-modal-header">
          <h2>Request Payout</h2>
          <button type="button" className="payout-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <p className="payout-modal-amount">Amount: <strong>₹{balance.toFixed(2)}</strong></p>

        <form onSubmit={handleSubmit} className="payout-form" noValidate>
          <label className="payout-field">
            <span>Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            {errors.email && <span className="payout-field-error">{errors.email}</span>}
          </label>

          <label className="payout-field">
            <span>Phone Number</span>
            <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="9876543210" maxLength={10} />
            {errors.phoneNumber && <span className="payout-field-error">{errors.phoneNumber}</span>}
          </label>

          <label className="payout-field">
            <span>UPI ID</span>
            <input type="text" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="name@upi" />
            {errors.upiId && <span className="payout-field-error">{errors.upiId}</span>}
          </label>

          <label className="payout-field">
            <span>Account Holder Name</span>
            <input type="text" value={accountHolderName} onChange={(e) => setAccountHolderName(e.target.value)} placeholder="As per bank account" />
            {errors.accountHolderName && <span className="payout-field-error">{errors.accountHolderName}</span>}
          </label>

          {submitError && <p className="payout-submit-error">{submitError}</p>}

          <button type="submit" className="payout-submit-btn" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RequestPayoutModal;
